import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { LockLocked, People } from '@factorialco/f0-react/icons/app'
import { DocumentsPageDataDocument, DocumentsPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import FilesCategoryList from 'modules/bookkeepers/components/PayrollCycle/FilesCategoryList'
import ExportedDocumentsCollection from 'modules/integrations/components/ExportedDocumentsCollection'

interface Props {
  payrollRunId: string
  hasProfessionals: boolean
  onImport?: (fileId: string) => void
  importedFileIds?: Set<string>
  hasCompensationsProfessionalsConnection?: boolean
}

const Documents = ({
  payrollRunId,
  hasProfessionals,
  onImport,
  importedFileIds,
  hasCompensationsProfessionalsConnection,
}: Props) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const lastFileCreatedAt = undefined

  const { loading: policiesLoading, data: policiesData } = useQuery(DocumentsPageDocument)

  const { loading: dataLoading, data } = useQuery(DocumentsPageDataDocument, {
    variables: {
      companyId,
      payrollRunId,
    },
    fetchPolicy: 'cache-and-network',
  })

  const sharedCategories =
    data?.compensations?.fileCategoriesConnection?.nodes?.filter(
      (category) => category.categoryType !== 'exported'
    ) ?? []
  const exportedCategory = data?.compensations?.fileCategoriesConnection?.nodes?.find(
    (category) => category.categoryType === 'exported'
  )

  if (policiesLoading || dataLoading) return <Loading />

  if (!policiesData?.compensations?._policies?.compensationActivityManage?.can) {
    return <NotFound />
  }

  const emptyStateSharedCategories = {
    'no-data': {
      title: i18n.t(
        'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.empty_state.title'
      ),
      description: i18n.t(
        'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.empty_state.description_documents'
      ),
    },
  }

  const emptyStateExportedCategory = {
    'no-data': {
      title: i18n.t(
        'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.empty_state.title'
      ),
      description: i18n.t(
        'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.empty_state.description_exports'
      ),
    },
  }

  return (
    <>
      {hasProfessionals && (
        <Box padding="s24" gap="s8">
          <Box flexDirection="row" padding="s0" alignItems="center">
            <F0Icon icon={People} size="lg" color="default" />
            <Text.Normal color="grey1000" weight="semibold">
              {i18n.t(
                'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.documents.title'
              )}
            </Text.Normal>
          </Box>
          <Box alignItems="stretch" paddingX="s24" paddingY="s0">
            {sharedCategories && sharedCategories.length > 0 && (
              <FilesCategoryList
                companyId={companyId}
                payrollRunId={payrollRunId}
                categories={sharedCategories}
                emptyState={emptyStateSharedCategories}
                refetchTrigger={lastFileCreatedAt}
                sidebarView
                onImport={onImport}
                importedFileIds={importedFileIds}
                hasCompensationsProfessionalsConnection={hasCompensationsProfessionalsConnection}
              />
            )}
          </Box>
        </Box>
      )}
      <Box padding="s24" gap="s8">
        <Box flexDirection="row" padding="s0">
          {hasProfessionals && <F0Icon icon={LockLocked} size="lg" color="default" />}
          <Text.Normal color="grey1000" weight="semibold">
            {i18n.t(
              'compensations.PayrollRunCompensationsList.activity_sidepanel.documents.exports.title'
            )}
          </Text.Normal>
        </Box>
        <Box alignItems="center" paddingX="s24">
          {exportedCategory && (
            <FilesCategoryList
              companyId={companyId}
              payrollRunId={payrollRunId}
              categories={[exportedCategory]}
              emptyState={emptyStateExportedCategory}
              refetchTrigger={lastFileCreatedAt}
              sidebarView
            />
          )}
        </Box>
      </Box>
      <ExportedDocumentsCollection
        filterParameters={[{ key: 'payroll_run_id', value: payrollRunId.toString() }]}
      />
    </>
  )
}

export default Documents
